import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { OPEN_SHIFTS, SHADOW, SURFACE } from "../../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest, RequestOrigins } from "../../../../utils/models";
import { countNurseResponses } from "../../../../utils/utilFunctions";
import { GapsRequestAnswers } from "./GapsRequestAnswers";
import { GapsRequestCardHeader } from "./GapsRequestCardHeader";
import { type GapsApplicant, useGapsApplicants } from "./useGapsApplicants";
import { useGapsRequestLabels } from "./useGapsRequestLabels";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** Answered, and starting inside the urgent window (`isUrgentClaim`) — the 48h band's cards. */
	isUrgentClaim: boolean;
	/**
	 * Shows its answers without being asked: an urgent claim, or a list narrowed to answered requests (the artifact's
	 * rule). Followed live — switching to the Answered filter opens the cards already on screen, and switching back folds
	 * the ones the filter opened — while a card the user opened stays open.
	 */
	isExpandedByList: boolean;
	/** A robo call is already out for this request (the call-off agent's own list). */
	isRoboCallRunning: boolean;
	/** Why the request exists; `null` where the department does not get origins. */
	origins: RequestOrigins | null;
	/** "Now", as the list last read it (it moves on each minute), so every card measures "starts in" from the same instant. */
	now: DateTime;
	onOpenRequest: (request: OpenShiftRequest | ReplaceShiftRequest) => void;
	onApprove: (request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant) => void;
};

/**
 * One posted request on Open Shifts (P3-12), drawn as the design's Option A (APP-886): a white card on the list's tint,
 * the shift's hours as its title, a line with the job, "Need N · N responses" and when it starts, and — once it is
 * answered — the responses hanging off a rail, each with a labelled Approve, three at first and the rest behind
 * "Show N more".
 *
 * The headline is the request itself: tapping it opens the posted-shift sheet (escalate, agency, incentive, hours,
 * how many, add someone, delete). The answers below it are the decision, so an urgent claim — answered and starting
 * within 48 hours — opens with them showing and its "Starts in" is the amber chip; any other answered request folds them
 * behind "Show responses" until asked, which is also when their overtime is fetched.
 */
export const GapsRequestCard: FC<Props> = ({ request, isUrgentClaim, isExpandedByList, isRoboCallRunning, origins, now, onOpenRequest, onApprove }) => {
	const { hoursLabel, sectionsOf } = useGapsRequestLabels();
	// A request for more than one job: each response then says which it is, since the card's chips name several.
	const isMultiJob = useMemo(() => sectionsOf(request).length > 1, [sectionsOf, request]);
	// Derived, not seeded: cards are keyed by request, so a flag read once at mount never saw the list switch to Answered.
	// The card has no fold control, so the user's own "see who" only ever opens it. The overtime this fetches is one
	// batch per opened card — the classic list predicts once per applicant on every card it draws.
	const [isOpenedByUser, setIsOpenedByUser] = useState<boolean>(false);
	const isExpanded = isOpenedByUser || isExpandedByList;
	const responseCount = countNurseResponses(request);
	const { applicants } = useGapsApplicants(request, isExpanded && responseCount > 0);

	const testId = `gaps-request-${request.id}`;
	// Every card that has not started says when it does (Option A); a running night shift, or an invalid start (which
	// `toRelative` names no time for), says nothing.
	const start = request.message.shift_start;
	const startsIn = start.isValid && start > now ? start.toRelative({ base: now }) : null;

	return (
		<Box
			data-testid={testId}
			data-urgent={isUrgentClaim}
			sx={{
				bgcolor: SURFACE,
				borderRadius: OPEN_SHIFTS.CARD_RADIUS,
				boxShadow: SHADOW.PANEL_SOFT,
				overflow: "hidden",
			}}
		>
			<GapsRequestCardHeader
				request={request}
				isUrgentClaim={isUrgentClaim}
				startsIn={startsIn}
				responseCount={responseCount}
				isRoboCallRunning={isRoboCallRunning}
				origins={origins}
				onOpenRequest={onOpenRequest}
				testId={testId}
			/>
			<GapsRequestAnswers
				request={request}
				responseCount={responseCount}
				isExpanded={isExpanded}
				applicants={applicants}
				onReview={() => setIsOpenedByUser(true)}
				formatHours={hoursLabel}
				onApprove={onApprove}
				isMultiJob={isMultiJob}
				testId={testId}
			/>
		</Box>
	);
};
